import {
  buildProtocolRuleCells,
  getRuleTotal
} from '../ProtocolRuleGrid/ProtocolRuleGrid.utils';

import type {
  ProtocolRulePeriodRequest,
  ProtocolRuleRequest,
  ProtocolRuleResponse
} from 'generated/index';
import type {
  PhaseDescription,
  ProtocolRuleFormValues,
  ProtocolRulePhaseValues,
  RuleDescription
} from './ProtocolRuleForm.types';

/** How far a protocol may run; the backend enforces the same bound */
export const MAX_PROTOCOL_WEEKS = 104;

/** Weeks a new follow-up phase runs by default */
const NEW_PHASE_WEEKS = 4;

/**
 * The first phase starts in week 1 with its end week still to be filled in: there is no "until the
 * end of the protocol", since the duration follows from the rules. A next phase starts the week after
 * the previous one and runs four weeks.
 */
export const newPhase = (
  previous?: ProtocolRulePhaseValues
): ProtocolRulePhaseValues => {
  const startWeek = previous ? (previous.endWeek ?? 0) + 1 : 1;
  return {
    startWeek,
    endWeek: previous ? startWeek + NEW_PHASE_WEEKS - 1 : null,
    recurrenceType: 'WEEKLY',
    amount: 1,
    interval: 1
  };
};

export const toProtocolRuleFormValues = (
  rule?: ProtocolRuleResponse
): ProtocolRuleFormValues => ({
  activity: rule?.activity ?? null,
  phases: rule
    ? rule.periods.map((period) => ({
        startWeek: period.startPeriodNumber,
        endWeek: period.endPeriodNumber,
        recurrenceType: period.recurrenceType,
        amount: period.frequency,
        interval: period.interval
      }))
    : [newPhase()],
  description: rule?.description ?? ''
});

/** Only called on values the schema accepted, so every number is filled in */
const toPeriod = ({
  startWeek,
  endWeek,
  recurrenceType,
  amount,
  interval
}: ProtocolRulePhaseValues): ProtocolRulePeriodRequest => ({
  startPeriodNumber: startWeek as number,
  endPeriodNumber: endWeek as number,
  recurrenceType,
  frequency: amount as number,
  interval: recurrenceType === 'TOTAL' ? 1 : (interval as number)
});

export const toProtocolRuleRequest = ({
  activity,
  phases,
  description
}: ProtocolRuleFormValues): ProtocolRuleRequest => ({
  activityId: activity?.entityId ?? '',
  description: description.trim() || undefined,
  periods: phases.map(toPeriod)
});

/**
 * Indexes of the phases that start on or before the end of the phase before them in week order. The
 * later phase of a pair is the one reported, so the message lands where the overlap was made.
 */
export const findOverlappingPhases = (
  phases: ProtocolRulePhaseValues[]
): number[] => {
  const complete = phases
    .map((phase, index) => ({ phase, index }))
    .filter(({ phase }) => phase.startWeek !== null && phase.endWeek !== null)
    .sort(
      (a, b) => (a.phase.startWeek as number) - (b.phase.startWeek as number)
    );

  return complete
    .filter(
      ({ phase }, position) =>
        position > 0 &&
        (phase.startWeek as number) <=
          (complete[position - 1].phase.endWeek as number)
    )
    .map(({ index }) => index);
};

const isWholeNumber = (value: number | null): value is number =>
  value !== null && Number.isInteger(value);

const isComplete = ({
  startWeek,
  endWeek,
  recurrenceType,
  amount,
  interval
}: ProtocolRulePhaseValues) =>
  isWholeNumber(startWeek) &&
  isWholeNumber(endWeek) &&
  startWeek >= 1 &&
  endWeek >= startWeek &&
  endWeek <= MAX_PROTOCOL_WEEKS &&
  isWholeNumber(amount) &&
  amount >= 1 &&
  (recurrenceType === 'TOTAL' || (isWholeNumber(interval) && interval >= 1));

const describePhase = (
  period: ProtocolRulePeriodRequest
): PhaseDescription => ({
  kind:
    period.recurrenceType === 'TOTAL'
      ? 'total'
      : period.interval === 1
      ? 'weekly'
      : 'everyWeeks',
  amount: period.frequency,
  interval: period.interval,
  start: period.startPeriodNumber,
  end: period.endPeriodNumber
});

/** One phase as the summary describes it ("week 1 t/m week 4 6× per week"); null while incomplete */
export const describePhaseValues = (
  phase: ProtocolRulePhaseValues
): PhaseDescription | null =>
  isComplete(phase) ? describePhase(toPeriod(phase)) : null;

/**
 * What the live summary says: every phase in turn and the sessions over the whole rule. The total is
 * counted with the grid's own cells, so the summary and the grid can never disagree. Null while a
 * phase is still incomplete.
 */
export const describeRule = (
  phases: ProtocolRulePhaseValues[]
): RuleDescription | null => {
  if (phases.length === 0 || !phases.every(isComplete)) {
    return null;
  }
  const periods = phases.map(toPeriod);
  const weekCount = Math.max(
    ...periods.map((period) => period.endPeriodNumber)
  );
  return {
    phases: periods.map(describePhase),
    total: getRuleTotal(buildProtocolRuleCells({ periods }, weekCount))
  };
};
